import { jsx, jsxs } from "react/jsx-runtime";
import { css } from "@/styled-system/css";
import { Tooltip, Paragraph, Anchor } from "@zillow/constellation-10";
import { event } from "@zillow/universal-analytics";
import { yieldCallback } from "@zillow/yield-callback";
import { TextInline, usePageProps, migrateColorToken, getCmsInfoForPage } from "@zillow/zrs-cms";
import * as HtmlToReact from "html-to-react";
import { omit } from "lodash";
import { withTrigger } from "./helpers.esm.js";
import { usePopulateParamPlaceHolders } from "./populateParamPlaceholders.esm.js";
const HtmlToReactParser = HtmlToReact.Parser;
const AnchorWithTrigger = withTrigger(Anchor);
const htmlToReactParser = HtmlToReactParser();
const processNodeDefinitions = HtmlToReact.ProcessNodeDefinitions();
const supClassName = css({
  fontSize: "[0.625em]",
  lineHeight: "[0]"
});
function findDirectParent(node, predicate) {
  const parent = node.parent;
  return parent && predicate(parent) ? parent : null;
}
function findDirectChild(node, predicate) {
  if (!node.children) return null;
  for (const child of node.children) {
    if (predicate(child)) {
      return child;
    }
  }
  return null;
}
function processingInstructions({
  _blockAttributes
}) {
  return [
    // Spans
    {
      shouldProcessNode(node) {
        var _a;
        return node.name === "span" && !!((_a = node.attribs) == null ? void 0 : _a.color);
      },
      processNode(node, children, nodeIndex) {
        const ProcessedSpan = () => {
          var _a;
          return /* @__PURE__ */ jsx(TextInline, { fontColor: ((_a = node.attribs) == null ? void 0 : _a.color) ?? "", children }, `${nodeIndex}-span-${node.name}`);
        };
        return /* @__PURE__ */ jsx(ProcessedSpan, {});
      }
    },
    // Links
    {
      shouldProcessNode(node) {
        return node.name === "a";
      },
      processNode(node, children, nodeIndex) {
        const CustomAnchor = () => {
          var _a, _b;
          const pageProps = usePageProps();
          const href = usePopulateParamPlaceHolders(node.attribs.href);
          const parentColorSpan = findDirectParent(node, (parent) => {
            var _a2;
            return parent.name === "span" && !!((_a2 = parent.attribs) == null ? void 0 : _a2.color);
          });
          const childColorSpan = findDirectChild(node, (child) => {
            var _a2;
            return child.name === "span" && !!((_a2 = child.attribs) == null ? void 0 : _a2.color);
          });
          const inheritedColor = ((_a = childColorSpan == null ? void 0 : childColorSpan.attribs) == null ? void 0 : _a.color) || ((_b = parentColorSpan == null ? void 0 : parentColorSpan.attribs) == null ? void 0 : _b.color);
          const migratedColor = inheritedColor ? migrateColorToken(inheritedColor) : void 0;
          const anchorProps = {
            triggerLink: node.attribs.href,
            key: `${nodeIndex}-anchor-${node.name}`,
            ...omit(node.attribs, "style", "class"),
            href,
            css: {
              // 'anywhere' is valid CSS but not in Panda's known values — escape hatch
              overflowWrap: "[anywhere]",
              wordBreak: "break-word",
              ...migratedColor && {
                color: migratedColor,
                // !important (via Panda's `!` suffix) needed to beat v10 Anchor recipe's own _visited/_hover rules
                _visited: {
                  color: `${migratedColor}!`
                },
                _hover: {
                  color: `${migratedColor}!`
                }
              }
            }
          };
          return /* @__PURE__ */ jsx(AnchorWithTrigger, { ...anchorProps, onMouseEnter: (e) => {
            event({
              // https://dataportal.zillowgroup.net/event-registry/2049
              envelope: {
                event_type_id: "2049",
                event_type_version_id: "1",
                event_template_id: "126",
                event_template_version_id: "1"
                // Master ID: 2284.2.126.1
              },
              clickstream_trigger: {
                trigger_type_nm: "interaction",
                trigger_location_nm: "cms",
                trigger_source_nm: "cms_anchor",
                trigger_object_nm: "no_trigger_object"
              },
              semantic: {
                semantic_event_nm: "cms_hover"
              },
              cms_info: {
                block_id: e.target.id || "",
                block_type_cd: "anchor",
                block_displayed_txt: e.target.textContent,
                block_target_url: href,
                block_target_window_txt: node.attribs.target || "",
                ...getCmsInfoForPage(pageProps)
              }
            });
          }, onClick: yieldCallback((e) => {
            event({
              // https://dataportal.zillowgroup.net/event-registry/2048
              envelope: {
                event_type_id: "2048",
                event_type_version_id: "1",
                event_template_id: "126",
                event_template_version_id: "1"
                // Master ID: 2284.2.126.1
              },
              clickstream_trigger: {
                trigger_type_nm: "interaction",
                trigger_location_nm: "cms",
                trigger_source_nm: "cms_anchor",
                trigger_object_nm: "no_trigger_object"
              },
              semantic: {
                semantic_event_nm: "cms_click"
              },
              cms_info: {
                block_id: e.target.id || "",
                block_type_cd: "anchor",
                block_displayed_txt: e.target.textContent,
                block_target_url: node.attribs.href,
                block_target_window_txt: node.attribs.target || "",
                ...getCmsInfoForPage(pageProps)
              }
            });
          }), children });
        };
        return /* @__PURE__ */ jsx(CustomAnchor, {}, `${nodeIndex}-anchor-container-${node.name}`);
      }
    },
    // Tooltips
    {
      shouldProcessNode(node) {
        var _a;
        return ((_a = node.attribs) == null ? void 0 : _a.class) === "constellation-tooltip";
      },
      processNode(node, children, nodeIndex) {
        const TooltipContainer = () => {
          const pageProps = usePageProps();
          return /* @__PURE__ */ jsxs(Tooltip.Root, { useClickProps: {
            enabled: true
          }, useHoverProps: {
            enabled: false
          }, onOpenChange: (open) => {
            var _a, _b;
            if (open) {
              event({
                // https://dataportal.zillowgroup.net/event-registry/5030
                envelope: {
                  event_type_id: "5030",
                  event_type_version_id: "1",
                  event_template_id: "297",
                  event_template_version_id: "1"
                  // Master ID: 2284.2.126.1
                },
                clickstream_trigger: {
                  trigger_type_nm: "interaction",
                  trigger_location_nm: "cms",
                  trigger_source_nm: "cms_tooltip",
                  trigger_object_nm: "no_trigger_object"
                },
                semantic: {
                  semantic_event_nm: "cms_open"
                },
                cms_info: {
                  block_id: ((_a = node == null ? void 0 : node.attribs) == null ? void 0 : _a.id) || "",
                  block_type_cd: "tooltip",
                  block_displayed_txt: children[0] || "",
                  ...getCmsInfoForPage(pageProps)
                }
              });
            } else {
              event({
                // https://dataportal.zillowgroup.net/event-registry/2051
                envelope: {
                  event_type_id: "2051",
                  event_type_version_id: "1",
                  event_template_id: "126",
                  event_template_version_id: "1"
                  // Master ID: 2284.2.126.1
                },
                clickstream_trigger: {
                  trigger_type_nm: "interaction",
                  trigger_location_nm: "cms",
                  trigger_source_nm: "cms_tooltip",
                  trigger_object_nm: "no_trigger_object"
                },
                semantic: {
                  semantic_event_nm: "cms_close"
                },
                cms_info: {
                  block_id: ((_b = node == null ? void 0 : node.attribs) == null ? void 0 : _b.id) || "",
                  block_type_cd: "tooltip",
                  block_displayed_txt: children[0] || "",
                  ...getCmsInfoForPage(pageProps)
                }
              });
            }
          }, children: [
            /* @__PURE__ */ jsx(Tooltip.Trigger, {}),
            /* @__PURE__ */ jsx(Tooltip.Portal, { children: /* @__PURE__ */ jsx(Tooltip.Content, { children: /* @__PURE__ */ jsx(Paragraph, { children }) }) })
          ] }, `${nodeIndex}-tooltip-${node.name}`);
        };
        return /* @__PURE__ */ jsx(TooltipContainer, {});
      }
    },
    // Superscript
    {
      shouldProcessNode(node) {
        return node.name === "sup";
      },
      processNode(_node, children, nodeIndex) {
        return /* @__PURE__ */ jsx("sup", { className: supClassName, children }, `${nodeIndex}-sup`);
      }
    },
    {
      // Anything else
      shouldProcessNode() {
        return true;
      },
      processNode: processNodeDefinitions.processDefaultNode
    }
  ];
}
function makeContentInteractive(content, options = {}) {
  return htmlToReactParser.parseWithInstructions(content, () => true, [...options.extraInstructions || [], ...processingInstructions({
    ...options
  })]);
}
export {
  makeContentInteractive as default
};
